import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FieldTypes, nameRegexValidator, urlRegexValidator } from "utils/formUtils";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import message from "components/CustomMessage";
import BaseModalForm from "components/BaseModalForm";
import _ from "lodash";
import { Form } from "antd";
import ModalUtil from "utils/modalUtil";
import { Alert } from "antd";
import { duplicateNameValidator } from "utils/validatorUtil";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

export default forwardRef(function Device42ModalForm(
  { configData, name = "Device42", afterClose, ...props },
  ref
) {
  const [loginId, setLoginID] = useState(configData?.id);
  const [form] = Form.useForm();
  const { configList } = props;
  const featureFlags = useFeatureFlag();

  const addCreds = useCallback(async () => {
    const data = form.getFieldsValue();
    const api = loginId
      ? ToolsIntegrationsAPI.updateGenericLoginDetail(data, name, loginId)
      : ToolsIntegrationsAPI.postGenericLoginDetail(data, name);
    const response = await api.promise;
    setLoginID(response.id);
    message.success(
      loginId
        ? "Credentials updated successfully"
        : "Credentials added successfully"
    );
    if(featureFlags.RBVM_ASSETS){
      ModalUtil.show({
        content: (
          <AssetForm
            response={data}
            loginId={response.id}
            name={name}
            onUpdateConfigComponent={props.onUpdateConfigComponent}
            configData={configData}
          />
        ),
      });
    }
  }, [form, name, loginId, configData, props, featureFlags.RBVM_ASSETS]);

  const fields = useMemo(
    () => [
      {
        name: "name",
        label: "Configuration Name",
        NoDefaultLengthRule: true,
        rules: [
          nameRegexValidator,
          duplicateNameValidator({ list: configList }),
        ],
        props: {
          autoFocus: true,
        },
        required: true,
      },
      {
        name: "apiEndpoint",
        label: "API Endpoint",
        required: true,
        rules: [urlRegexValidator],
      },
      {
        name: "username",
        label: "Username",
        required: true,
      }, 
      {
        name: "password",
        label: "Password",
        type: FieldTypes.PASSWORD,
        required: true,
      }
    ],
    [configList]
  );

  return (
    <BaseModalForm
      title={
        !_.isEmpty(configData)
          ? `Edit ${name} Configuration`
          : `Configure ${name}`
      }
      ref={ref}
      form={form}
      fields={fields}
      onOk={addCreds}
      initialValues={configData}
      afterClose={afterClose}
    ></BaseModalForm>
  );
});

const AssetForm = ({
  response,
  configData,
  name,
  onUpdateConfigComponent,
  loginId,
}) => {
  const [checked, setChecked] = useState(!configData?.customer);
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState();
  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(name, {
        login_id: loginId || response?.id,
      });
    promise.then((res) =>
      setCustomers(res.projects.map((i) => ({ label: i.name, value: i.name })))
    );
    return cancel;
  }, [name, loginId, response?.id]);

  const onCheck = useCallback(
    (e) => {
      form.setFieldsValue({ customer: undefined });
      setChecked(e.target.checked);
    },
    [form]
  );
  const assetFields = useMemo(
    () => [
      {
        name: "fetch",
        label: "Fetch All Assets",
        type: FieldTypes.BOOLEAN,
        inline: true,
        props: {
          onChange: onCheck,
          value: checked,
        },
      },
      {
        name: "customer",
        label: "Customer",
        type: FieldTypes.SELECT,
        disabled: checked,
        options: customers,
      },
      {
        name: "",
        label: "",
        render: () => (
          <Alert
            type="warning"
            className="m-b-xs"
            showIcon
            message={`If the form is not saved, no assets will be fetched`}
          />
        ),
      },
    ],
    [customers, checked, onCheck]
  );
  const submitData = useCallback(
    async (data) => {
      delete data.fetch;
      let finalData = {
        ...response,
        ...data,
        ...{ fetchAssets: true },
      };
      await ToolsIntegrationsAPI.updateGenericLoginDetail(
        finalData,
        name,
        loginId
      ).promise;
      if (configData?.id) {
        message.success("Configuration updated successfully");
      } else {
        message.success(`Successfully configured.`);
      }
      onUpdateConfigComponent();
    },
    [configData, onUpdateConfigComponent, response, loginId, name]
  );
  return (
    <BaseModalForm
      fields={assetFields}
      initialValues={{
        ...configData,
        fetch: !configData?.customer,
      }}
      form={form}
      onOk={submitData}
      title={"Add Asset Criteria"}
    ></BaseModalForm>
  );
};
